import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
/* eslint-disable camelcase */
import { required, email, image, ext, size, min, max, confirmed, numeric, alpha, alpha_num, regex, between, max_value, min_value, excluded } from 'vee-validate/dist/rules'

import Vue from 'vue'
import { isValidDomainEntry } from './domain.js'

Vue.component('validation-provider', ValidationProvider)
Vue.component('validation-observer', ValidationObserver)

extend('email', {
	...email,
	message: 'Please enter a valid email'
})
extend('requiredEmails', {
	...required,
	message: 'At least one email address is required'
})
extend('required', {
	...required,
	message: 'This field is required'
})
extend('requiredBrands', {
	...required,
	message: 'At least one brand is required'
})

extend('requiredCookieScripts', {
	...required,
	message: 'At least one cookie script is required'
})

extend('requiredCookieCategories', {
	...required,
	message: 'At least one cookie category is required'
})
extend('requiredStrictlyNecessaryCategory', {
	...required,
	message: 'Strictly Necessary category is required'
})
extend('requiredDomains', {
	...required,
	message: 'At least one domain is required'
})

extend('image', {
	...image,
	message: 'Please upload a valid image'
})

extend('numeric', {
	...numeric,
	message: 'This field must be a positive number'
})

extend('alpha', {
	...alpha,
	message: 'This field must only contain letters'
})
/* eslint-disable camelcase */
extend('alpha_num', {
	...alpha_num,
	message: 'This field must only contain letters and numbers'
})

extend('regex', {
	...regex,
	message: 'Invalid characters - please check your input'
})

extend('size', {
	...size,
	message: (_, { size }) => {
		return `Image size must be less than ${size} `
	}
})
extend('min', {
	...min,
	message: (_, { length }) => {
		return `At least ${length} characters required`
	}
})
extend('max', {
	...max,
	message: (_, { length }) => {
		return `This field must contain less than ${length} characters`
	}
})
extend('max_value', {
	...max_value,
	message: (_, { max }) => {
		return `Value must not exceed ${max}`
	}
})
extend('min_value', {
	...min_value,
	message: (_, { min }) => {
		return `Value must not be less than ${min}`
	}
})
extend('betweenMinutes', {
	...between,
	message: (_, { min, max }) => {
		return `Value must be between ${min} and ${max} minutes`
	}
})
extend('confirmed', {
	...confirmed,
	message: (_, { target }) => {
		return `This field must match ${target}`
	}
})

extend('arrayIsNotEmpty', {
	params: ['value', 'ignoreVModel'],
	validate: (inputValue, { value: customValue, ignoreVModel }) => {
		const check = value => !!value && Array.isArray(value) && !!value.length
		return ignoreVModel ? check(customValue) : check(inputValue)
	},
	message: 'You must select at least one'
})

extend('ipRange', {
	// eslint-disable-next-line vue/max-len
	validate: value => value.match(/^(((25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?))((\s?-\s?(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?)\.(25[0-5]|2[0-4]\d|[01]?\d\d?))|(\/\d?\d))?)$/),
	message: 'This field must be a single ip address, or a range such as 65.34.77.0 - 65.34.77.9 or 65.34.77.0/24'
})

extend('isLink', {
	validate: value => value.match(/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]/),
	message: 'This field must be a valid domain'
})
extend('isUrl', {
	validate: value => value.match(/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]/),
	message: 'This field must be a valid URL'
})
// AB#27244 - stricter than isLink, which is unanchored and so accepts 'web.mc.syrenis.'.
// Mirrors what the Cassie Identity Service accepts, so a saved entry cannot 400 the CDC call.
extend('isDomainOrWildcard', {
	validate: value => isValidDomainEntry(value),
	message: 'The domain you\'re trying to add is invalid'
})
extend('isUrlwithHttp', {
	validate: value => value.match(/^(http:\/\/|https:\/\/)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]/),
	message: 'This field must must be a valid URL and start with http:// or https://'
})
extend('phoneNumber', {
	// eslint-disable-next-line no-useless-escape
	validate: value => value.match(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/),
	message: 'This field must must be a valid mobile or telephone number'
})

extend('isValidScript', {
	...ext,
	message: 'Please upload a valid script file'
})

extend('isValidCss', {
	...ext,
	message: 'Please upload a valid CSS file'
})

extend('isValidResource', {
	...ext,
	message: 'Please upload a valid resource file type (.png, .jpg, .jpeg, .gif, .svg, .ttf)'
})

extend('forbiddenChars', {
	params: ['value'],
	validate: (inputValue, { value: forbiddenChars }) => {
		return inputValue.split('').every(char => !forbiddenChars.includes(char))
	},
	message: (_, { value }) => {
		return `These characters are forbidden: ${value.join(', ')}`
	}
})
extend('duplicateValues', {
	params: ['value'],
	validate: (inputValue, { value: duplicateValues }) => {
		return !duplicateValues.includes(inputValue)
	},
	message: 'This value has already been provided'
})

extend('isDirectAuthWithPii', {
	params: ['value'],
	validate (value, { value: isDirectAuthWithPii }) {
		return value > 0 && !(value === 1 && isDirectAuthWithPii)
	},
	message: 'Direct authentication cannot be used if any PII is used on the Preference Page. Please select a different Authentication Type or remove PII from the form in the Build step.'
})

extend('validateGtmConsentTypeInService', {
	params: ['serviceId', 'gtmUsageInAssociatedCollections', 'gtmConsentTypes'],
	validate (value, { serviceId, gtmUsageInAssociatedCollections, gtmConsentTypes }) {
		if (!value) return true

		const usage = gtmUsageInAssociatedCollections.filter(usage => usage.gtmScripts.some(script => script.scriptId !== serviceId && script.consentTypeId === value))
		if (usage.length === 0) return true

		const gtmConsentType = gtmConsentTypes.find(type => type.value === value)?.text || 'Unknown Consent Type'
		if (usage.length === 1) {
			const existingService = usage[0].gtmScripts.find(script => script.scriptId !== serviceId && script.consentTypeId === value)
			return `Cannot set consent type '${gtmConsentType}'. Already used by service<br />'${existingService.scriptName}' (ID ${existingService.scriptId}) in collection '${usage[0].templateName}' (ID ${usage[0].templateId}).<br />Remove or change the conflicting service before saving.`
		}

		const errorMessages = `Cannot set consent type '${gtmConsentType}'. Already used in the following collections:`
		const affected = usage.map(usageItem => {
			return `<br />• ${usageItem.templateName} (${usageItem.templateId}) > ${usageItem.gtmScripts.filter(script => script.consentTypeId === value).map(script => `${script.categoryName} (${script.categoryId}) > ${script.scriptName} (${script.scriptId})`).join(', ')}`
		})
		return errorMessages + affected.join('') + '<br />Resolve these duplicates before saving or create a new service.'
	}
})

extend('validateGtmConsentTypesInCollection', {
	params: ['gtmConsentTypes', 'allScripts', 'inUse'],
	validate (value, { gtmConsentTypes, allScripts, inUse }) {
		const selectedGtmScripts = allScripts.filter(script => value.includes(script.id) && script.gtmEnabled)

		const clashingSelections = selectedGtmScripts.filter(script => selectedGtmScripts.some(otherScript =>
			script.id !== otherScript.id && script.gtmConsentTypeId === otherScript.gtmConsentTypeId
		))

		const groupedClashingSelections = clashingSelections.reduce((acc, clash) => {
			const existing = acc.find(group => group.gtmConsentTypeId === clash.gtmConsentTypeId)
			if (existing) {
				existing.scripts.push(clash)
			} else {
				acc.push({
					gtmConsentTypeId: clash.gtmConsentTypeId,
					gtmConsentType: gtmConsentTypes.find(type => type.value === clash.gtmConsentTypeId)?.text || 'Unknown Consent Type',
					scripts: [clash]
				})
			}
			return acc
		}, [])

		if (groupedClashingSelections.length > 0) {
			const errorMessages = groupedClashingSelections.map(group => {
				const scriptNames = group.scripts.map(script => script.cookieScriptName).join(', ')
				return `GTM Consent Type ${group.gtmConsentType} is used by multiple selected services: ${scriptNames}`
			})
			return errorMessages.join('<br />')
		}

		const clashesWithExistingServices = selectedGtmScripts
			.map(selectedScript => {
				const usedScript = inUse.find(
					used => used.gtmConsentTypeId === selectedScript.gtmConsentTypeId && used.scriptId !== selectedScript.id
				)
				return usedScript
					? { ...selectedScript, gtmConsentType: usedScript.gtmConsentType, existingScript: usedScript.scriptName, existingCategory: usedScript.categoryName }
					: null
			})
			.filter(Boolean)
		if (clashesWithExistingServices.length === 0) return true

		const groupedByConsentType = clashesWithExistingServices.reduce((acc, clash) => {
			const existing = acc.find(group => group.gtmConsentTypeId === clash.gtmConsentTypeId)
			if (existing) {
				existing.scripts.push(clash)
			} else {
				acc.push({
					gtmConsentTypeId: clash.gtmConsentTypeId,
					gtmConsentType: clash.gtmConsentType,
					scripts: [clash]
				})
			}
			return acc
		}, [])

		const errorHeading = clashesWithExistingServices.length === 1
			? 'You have selected a service with a Google consent type that is already in use in this collection:'
			:	'You have selected services with Google consent types that are already in use in this collection:'
		const errorMessages = groupedByConsentType.map(group => {
			const scriptDetails = group.scripts.map(script =>
				`<br />- '${script.cookieScriptName}' (ID ${script.id}) in this collection already uses consent type '${group.gtmConsentType}' in category '${script.existingCategory}'`
			)
			return scriptDetails
		}).flat()
		return errorHeading + errorMessages.join('')
	}
})

extend('requiredPreferenceLinkStatement', {
	validate (value) {
		return value?.length === 0
	},
	message: 'A Preference Link requires at least one Statement'
})

extend('classOrId', {
	validate (value) {
		return Array.from(value)[0] === '.' || Array.from(value)[0] === '#'
	},
	message: 'This field must be a valid class or id'
})

extend('decimal', {
	validate: value => value.match(/^0$|^[1-9]\d*$|^\.\d+$|^0\.\d*$|^[1-9]\d*\.\d*$/),
	message: 'This field must be a decimal number'
})

extend('hexSalt', {
	validate: value => {
		if (!value) return true
		// Check if valid hex characters (pattern matches encryption-utils.js)
		if (!/^[0-9a-fA-F]+$/.test(value)) return false
		// Check minimum length
		if (value.length < 16) return false
		return true
	},
	message: (_, { _value_ }) => {
		if (_value_ && !/^[0-9a-fA-F]+$/.test(_value_)) {
			return 'Salt must be hexadecimal'
		}
		return 'Salt must be at least 16 hex characters'
	}
})

extend('hexIv', {
	validate: value => {
		if (!value) return true
		// Check if valid hex characters (pattern matches encryption-utils.js)
		if (!/^[0-9a-fA-F]+$/.test(value)) return false
		// Check exact length
		if (value.length !== 32) return false
		return true
	},
	message: (_, { _value_ }) => {
		if (_value_ && !/^[0-9a-fA-F]+$/.test(_value_)) {
			return 'IV must be hexadecimal'
		}
		return 'IV must be exactly 32 hex characters'
	}
})

extend('password', {
	validate: value => value.match(/^(?=.*[A-Z])(?=.*\d)(?=.*[!#$%&'()*+,-./:;<=>?@^_`{|}~])[A-Za-z\d!#$%&'()*+,-./:;<=>?@^_`{|}~]{12,}$/),
	message: 'Password must meet the minimum strength requirement'
})

extend('greaterThanZero', {
	...required,
	validate (value) {
		return value > 0
	},
	message: 'This field must be greater than zero'
})
extend('wholeNumber', {
	...required,
	validate (value) {
		return /^[0-9]+$/.test(value)
	},
	message: 'This field must be a whole number'
})

extend('noUppercase', {
	validate: value => !/[A-Z]/.test(value),
	message: 'This field must not contain uppercase letters'
})

extend('inUseLinkFieldNames', {
	...excluded,
	message: 'This Name in Link is already in use'
})

extend('existingStyleNames', {
	...excluded,
	message: 'This Style Name is already in use'
})

extend('requiredFreeText', {
	...required,
	params: ['fieldLabel'],
	message: (_, { fieldLabel }) => {
		return `Please provide a value for ${fieldLabel}.`
	}
})

extend('notWhitespaceOnly', {
	validate: value => {
		if (!value) return true // Let 'required' rule handle empty values
		return value.trim().length > 0
	},
	message: 'This field cannot contain only whitespace'
})

extend('hasTwoDates', {
	validate (value) {
		if (!value || typeof value !== 'string') return 'A date range with a start and end date is required'
		const dates = value.split(' - ')
		return dates.length === 2 && dates[0].trim() && dates[1].trim()
			? true
			: 'A date range with a start and end date is required'
	}
})

extend('validateFromAndToDates', {
	params: ['fromDate', 'toDate', 'amendDateFrom', 'isFromDate'],
	validate (_, { fromDate, toDate, amendDateFrom, isFromDate }) {
		if (!fromDate || !toDate) return true // Let 'required' rule handle empty values. A value could be missing according to CS Portal configuration.
		if (isFromDate) {
			return true
		}
		const from = new Date(fromDate)
		const to = new Date(toDate)
		return from <= to || `Valid From ${!amendDateFrom ? '(' + fromDate + ')' : ''} must be earlier than or equal to Valid To`
	}
})
